import {obtenerIdTipoEnvios, TipoEnviosEnum} from "@shared/enums";
import {Nilable} from "@shared/utils";
import {PaisDTO} from "./Pais.DTO";

export interface ThirdPartyConf {
    successLink: string;
    screenoutLink: string;
    securityLink: string;
    quotafullLink: string;
}

export interface IEnvioDTO {
    id: number;
    type: string;
    campaign_id: number;
    campaign_name: string;
    combo_id: number;
    created_at: string;
    dispatch_datetime: string;
    target_country: string;
    enabled: boolean;
    enabled_until: Nilable<string>;
    is_rushable: boolean;
    priority: number;
    processed: boolean;
    qmpable: boolean;
    earned_score: number;
    sent_failed_count: number;
    to_send_count: number;
    confirmed_count: number;
    answered_count: number;
    available_users: number;
    throttle_surveys_per_day: Nilable<number>;
    updated_at: string;
    last_sending: string;
    research: number;
    auto_disable: boolean;
    public_id: string;
    third_party_conf?: ThirdPartyConf;
}

export class EnvioDTO {
    public id = 0;
    public tipo: TipoEnviosEnum = TipoEnviosEnum.STICKY_ALWAYS_ANYTIME;
    public grupoId = 0;
    public grupoNombre = "";
    public encuestaId = 0;
    public creadoEl = new Date();
    public fechaEnvio = new Date();
    public targetPais = "";
    public activo = false;
    public activadoHasta: Nilable<Date> = null;
    public esRushable = false;
    public prioridad = 1;
    public procesado = false;
    public esQmpable = true;
    public puntajeGanado = 0;
    public cantidadEnviosFallidos = 0;
    public cantidadAEnviar = 0;
    public cantidadConfirmados = 0;
    public cantidadRespuestas = 0;
    public usuariosDisponibles = 0;
    public throttle_surveys_per_day: Nilable<number> = null;
    public actualizadoEl = new Date();
    public ultimoEnvio = new Date();
    public investigacionId = 0;
    public auto_disable = false;
    public publicId: Nilable<string>;
    public thirdPartyConf?: ThirdPartyConf;

    constructor(json: Nilable<Partial<IEnvioDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IEnvioDTO>) {
        if (json.id != null) {
            this.id = json.id;
        }
        if (json.type != null) {
            this.tipo = TipoEnviosEnum[json.type];
        }
        if (json.campaign_id != null) {
            this.grupoId = json.campaign_id;
        }
        if (json.campaign_name != null) {
            this.grupoNombre = json.campaign_name;
        }
        if (json.combo_id != null) {
            this.encuestaId = json.combo_id;
        }
        if (json.created_at != null) {
            this.creadoEl = new Date(json.created_at);
        }
        if (json.dispatch_datetime != null) {
            this.fechaEnvio = new Date(json.dispatch_datetime);
        }
        if (json.target_country != null) {
            this.targetPais = json.target_country;
        }
        if (json.enabled) {
            this.activo = json.enabled;
        }
        if (json.enabled_until != null) {
            this.activadoHasta = new Date(json.enabled_until);
        }
        if (json.is_rushable) {
            this.esRushable = json.is_rushable;
        }
        if (json.priority != null) {
            this.prioridad = json.priority;
        }
        if (json.processed) {
            this.procesado = json.processed;
        }
        if (json.qmpable) {
            this.esQmpable = json.qmpable;
        }
        if (json.earned_score != null) {
            this.puntajeGanado = json.earned_score;
        }
        if (json.sent_failed_count != null) {
            this.cantidadEnviosFallidos = json.sent_failed_count;
        }
        if (json.to_send_count != null) {
            this.cantidadAEnviar = json.to_send_count;
        }
        if (json.confirmed_count != null) {
            this.cantidadConfirmados = json.confirmed_count;
        }
        if (json.answered_count != null) {
            this.cantidadRespuestas = json.answered_count;
        }
        if (json.available_users != null) {
            this.usuariosDisponibles = json.available_users;
        }
        if (json.throttle_surveys_per_day != null) {
            this.throttle_surveys_per_day = json.throttle_surveys_per_day;
        }
        if (json.updated_at != null) {
            this.actualizadoEl = new Date(json.updated_at);
        }
        if (json.last_sending != null) {
            this.ultimoEnvio = new Date(json.last_sending);
        }
        if (json.research != null) {
            this.investigacionId = json.research;
        }
        if (json.auto_disable != null) {
            this.auto_disable = json.auto_disable;
        }
        if (json.third_party_conf != null) {
            this.thirdPartyConf = { ...json.third_party_conf };
        }
        this.publicId = json.public_id;
    }

    public toJSON(): IEnvioDTO {
        const baseJson: IEnvioDTO = {
            id: this.id,
            type: obtenerIdTipoEnvios(this.tipo),
            campaign_id: this.grupoId,
            campaign_name: this.grupoNombre,
            combo_id: this.encuestaId,
            created_at: this.creadoEl.toJSON(),
            dispatch_datetime: this.fechaEnvio.toJSON(),
            target_country: this.targetPais,
            enabled: this.activo,
            enabled_until: this.activadoHasta?.toJSON() ?? null,
            is_rushable: this.esRushable,
            priority: this.prioridad,
            processed: this.procesado,
            qmpable: this.esQmpable,
            earned_score: this.puntajeGanado,
            sent_failed_count: this.cantidadEnviosFallidos,
            to_send_count: this.cantidadAEnviar,
            confirmed_count: this.cantidadConfirmados,
            answered_count: this.cantidadRespuestas,
            available_users: this.usuariosDisponibles,
            throttle_surveys_per_day: this.throttle_surveys_per_day,
            updated_at: this.actualizadoEl.toJSON(),
            last_sending: this.ultimoEnvio.toJSON(),
            research: this.investigacionId,
            auto_disable: this.auto_disable,
            public_id: this.publicId ?? ""
        };

        if (this.thirdPartyConf) {
            baseJson.third_party_conf = { ...this.thirdPartyConf };
        }

        return baseJson;
    }
}

export interface IEnvioActivoDTO {
    serie_id: number;
    type: string;
    combo_id: number;
    combo_name: string;
    campaign_id: number;
    campaign_name: string;
    research_id: number;
    research_name: string;
    qmpable: boolean;
    rusheable: boolean;
    country: string;
    dispatch_datetime: string;
    to_sent_count: number;
    answered_count: number;
    sent_failed_count: number;
    priority: number;
    combo_score: number;
}

export class EnvioActivoDTO {
    public envioId: number;
    public tipo: TipoEnviosEnum;
    public encuestaId: number;
    public encuestaNombre: string;
    public investigacionId: number;
    public pais: PaisDTO;
    public investigacionNombre: string;
    public grupoId: number;
    public grupoNombre: string;
    public qmpable: boolean;
    public rusheable: boolean;
    public totalEnvios: number;
    public enviosFallados: number;
    public fechaEnvio: Date;
    public totalRespuestas: number;
    public prioridad = 0;
    public encuestaPuntaje = 0;

    constructor(json: Nilable<Partial<IEnvioActivoDTO>> = null) {
        if (json != null) {
            this.cargarJSON(json);
        }
    }

    public cargarJSON(json: Partial<IEnvioActivoDTO>) {
        if (json.serie_id != null) {
            this.envioId = json.serie_id;
        }
        if (json.type != null) {
            this.tipo = TipoEnviosEnum[json.type];
        }
        if (json.combo_id != null) {
            this.encuestaId = json.combo_id;
        }
        if (json.combo_name != null) {
            this.encuestaNombre = json.combo_name;
        }
        if (json.research_id != null) {
            this.investigacionId = json.research_id;
        }
        if (json.country != null) {
            this.pais = new PaisDTO({ name: json.country });
        }
        if (json.research_name != null) {
            this.investigacionNombre = json.research_name;
        }
        if (json.campaign_id != null) {
            this.grupoId = json.campaign_id;
        }
        if (json.campaign_name != null) {
            this.grupoNombre = json.campaign_name;
        }
        if (json.qmpable != null) {
            this.qmpable = json.qmpable;
        }
        if (json.rusheable != null) {
            this.rusheable = json.rusheable;
        }
        if (json.to_sent_count != null) {
            this.totalEnvios = json.to_sent_count;
        }
        if (json.sent_failed_count != null) {
            this.enviosFallados = json.sent_failed_count;
        }
        if (json.dispatch_datetime != null) {
            this.fechaEnvio = new Date(json.dispatch_datetime);
        }
        if (json.answered_count != null) {
            this.totalRespuestas = json.answered_count;
        }
        if (json.priority != null) {
            this.prioridad = json.priority;
        }
        if (json.combo_score != null) {
            this.encuestaPuntaje = json.combo_score;
        }
    }
}

export interface IEnvioAgrupadoDTO {
    series: Nilable<IEnvioDTO[]>;
}

export class EnvioAgrupadoDTO {
    public series: Nilable<EnvioDTO[]>;

    constructor(json: Nilable<Partial<IEnvioAgrupadoDTO>> = null) {
        if (json != null) {
            this.cargarJSON(json);
        }
    }

    public cargarJSON(json: Partial<IEnvioAgrupadoDTO>) {
        if (json.series != null) {
            this.series = json.series.map(serie => new EnvioDTO(serie));
        }
    }

    public toJSON(): IEnvioAgrupadoDTO {
        return {
            series: this.series?.map(serie => serie.toJSON()) || null
        };
    }
}